import { useQuery } from '@tanstack/react-query';
import { claimWarehouseFor } from '../warehouses/currentWarehouse';
import { getCurrentUser, type UserDto } from './api';

/**
 * Query key for the current user (`GET /api/v1/auth/me`). Login success and
 * logout both invalidate this so the UI reflects the new session immediately.
 */
export const CURRENT_USER_KEY = ['auth', 'me'] as const;

/**
 * The signed-in user, or `null` when anonymous. `null` is a normal resolved
 * value (a 401 from /me), not an error state — so components branch on the
 * data, not on `isError`.
 */
export function useCurrentUser() {
  return useQuery<UserDto | null>({
    queryKey: CURRENT_USER_KEY,
    queryFn: async ({ signal }) => {
      const user = await getCurrentUser(signal);
      // The stored warehouse belongs to a user, not to the device. Sign-out clears it, but a
      // session can end without one — an expired cookie, a closed tab, a shared handheld passed
      // to the next shift — and then the next person to sign in inherits it. Reconciled HERE
      // rather than in an effect because usePermissions is gated on this query: doing it here
      // means the first warehouse-scoped request of the session already carries the right
      // warehouse, or none.
      if (user) claimWarehouseFor(user.id);
      return user;
    },
    staleTime: 60 * 1000,
    retry: false,
  });
}
